import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Button,
  Divider,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Edit as EditIcon,
  Download as DownloadIcon,
  SwapHoriz as StatusIcon,
  Delete as DeleteIcon,
  Clear as ClearIcon,
} from '@mui/icons-material';
import { format } from 'date-fns';
import api from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { getImageUrl } from '../../utils/imageUtils';

interface SupplierOrder {
  id: number;
  order_number: string;
  supplier_id: number;
  supplier_name: string;
  supplier_contact: string;
  supplier_email: string;
  supplier_phone: string;
  supplier_address: string;
  order_date: string;
  status: string;
  notes: string;
  created_by_name: string;
  created_by_lastname: string;
  created_at: string;
  items: any[];
}

const SupplierOrderDetail: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { hasPermission, hasRole } = useAuth();

  const [order, setOrder] = useState<SupplierOrder | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Status dialog
  const [statusDialogOpen, setStatusDialogOpen] = useState(false);
  const [newStatus, setNewStatus] = useState('');
  const [updatingStatus, setUpdatingStatus] = useState(false);

  // Delete dialog
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // PDF
  const [downloadingPdf, setDownloadingPdf] = useState(false);

  // Image preview
  const [imagePreviewOpen, setImagePreviewOpen] = useState(false);
  const [previewImage, setPreviewImage] = useState<{url: string, name: string} | null>(null);

  const handleImagePreview = (imageUrl: string, productName: string) => {
    setPreviewImage({ url: imageUrl, name: productName });
    setImagePreviewOpen(true);
  };

  const handleCloseImagePreview = () => {
    setImagePreviewOpen(false);
    setPreviewImage(null);
  };

  useEffect(() => {
    if (id) fetchOrder();
  }, [id]);

  const fetchOrder = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/supplier-orders/${id}`);
      if (response.data.success) {
        setOrder(response.data.data);
      }
    } catch (error: any) {
      console.error('Error fetching order:', error);
      setError('Failed to load supplier order');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status: string): 'warning' | 'info' | 'success' | 'error' | 'default' => {
    switch (status) {
      case 'draft': return 'default';
      case 'pending': return 'warning';
      case 'sent': return 'success';
      case 'cancelled': return 'error';
      default: return 'default';
    }
  };

  const handleStatusChange = async () => {
    if (!newStatus || !order) return;
    try {
      setUpdatingStatus(true);
      await api.patch(`/supplier-orders/${order.id}/status`, { status: newStatus });
      await fetchOrder();
      setStatusDialogOpen(false);
      setNewStatus('');
    } catch (error: any) {
      console.error('Error updating status:', error);
      setError(error.response?.data?.message || 'Failed to update status');
    } finally {
      setUpdatingStatus(false);
    }
  };

  const handleDelete = async () => {
    if (!order) return;
    try {
      setDeleting(true);
      await api.delete(`/supplier-orders/${order.id}`);
      navigate('/supplier-orders');
    } catch (error: any) {
      console.error('Error deleting order:', error);
      setError(error.response?.data?.message || 'Failed to delete order');
    } finally {
      setDeleting(false);
      setDeleteDialogOpen(false);
    }
  };

  const handleDownloadPDF = async () => {
    if (!order) return;
    try {
      setDownloadingPdf(true);
      const response = await api.get(`/supplier-orders/${order.id}/pdf`, {
        responseType: 'blob',
        timeout: 30000,
      });

      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `supplier-order-${order.order_number}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error: any) {
      console.error('Error downloading PDF:', error);
      setError('Failed to download PDF');
    } finally {
      setDownloadingPdf(false);
    }
  };

  const canEdit = order && (hasPermission('supplier_orders.update') || hasRole(['super_admin', 'admin']));

  const canDelete = order && (hasPermission('supplier_orders.delete') || hasRole(['super_admin', 'admin']));

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  if (!order) {
    return (
      <Box p={3}>
        <Alert severity="error">Supplier order not found</Alert>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/supplier-orders')} sx={{ mt: 2 }}>
          Back to Orders
        </Button>
      </Box>
    );
  }

  // Calculate totals
  const totalBoxQty = (order.items || []).reduce((sum, item) => sum + (parseInt(item.box_quantity) || 0), 0);
  const totalSingleQty = (order.items || []).reduce((sum, item) => sum + (parseInt(item.single_quantity) || 0), 0);

  return (
    <Box>
      <Paper sx={{ p: 3, mb: 3 }}>
        {/* Header */}
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3} flexWrap="wrap" gap={2}>
          <Box display="flex" alignItems="center" gap={2}>
            <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/supplier-orders')}>
              Back
            </Button>
            <Typography variant="h4">
              {order.order_number}
            </Typography>
            <Chip
              label={order.status.toUpperCase()}
              color={getStatusColor(order.status)}
              size="small"
            />
          </Box>
          <Box display="flex" gap={1} flexWrap="wrap">
            <Button
              variant="outlined"
              startIcon={downloadingPdf ? <CircularProgress size={16} /> : <DownloadIcon />}
              onClick={handleDownloadPDF}
              disabled={downloadingPdf}
            >
              Download PDF
            </Button>
            {(hasPermission('supplier_orders.update') || hasRole(['super_admin', 'admin'])) && (
              <Button
                variant="outlined"
                startIcon={<StatusIcon />}
                onClick={() => {
                  setNewStatus(order.status);
                  setStatusDialogOpen(true);
                }}
              >
                Change Status
              </Button>
            )}
            {canEdit && (
              <Button
                variant="outlined"
                startIcon={<EditIcon />}
                onClick={() => navigate(`/supplier-orders/${order.id}/edit`)}
              >
                Edit
              </Button>
            )}
            {canDelete && (
              <Button
                variant="outlined"
                color="error"
                startIcon={<DeleteIcon />}
                onClick={() => setDeleteDialogOpen(true)}
              >
                Delete
              </Button>
            )}
          </Box>
        </Box>

        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

        {/* Info cards */}
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3, mb: 3 }}>
          <Card variant="outlined">
            <CardContent>
              <Typography variant="h6" gutterBottom>Supplier Information</Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: '120px 1fr', gap: 1 }}>
                <Typography color="text.secondary">Supplier:</Typography>
                <Typography fontWeight="medium">{order.supplier_name}</Typography>
                <Typography color="text.secondary">Contact:</Typography>
                <Typography>{order.supplier_contact || '-'}</Typography>
                <Typography color="text.secondary">Email:</Typography>
                <Typography>{order.supplier_email || '-'}</Typography>
                <Typography color="text.secondary">Phone:</Typography>
                <Typography>{order.supplier_phone || '-'}</Typography>
                {order.supplier_address && (
                  <>
                    <Typography color="text.secondary">Address:</Typography>
                    <Typography>{order.supplier_address}</Typography>
                  </>
                )}
              </Box>
            </CardContent>
          </Card>

          <Card variant="outlined">
            <CardContent>
              <Typography variant="h6" gutterBottom>Order Information</Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: '120px 1fr', gap: 1 }}>
                <Typography color="text.secondary">Order #:</Typography>
                <Typography fontWeight="medium">{order.order_number}</Typography>
                <Typography color="text.secondary">Order Date:</Typography>
                <Typography>{order.order_date ? format(new Date(order.order_date), 'MMM dd, yyyy') : '-'}</Typography>
                <Typography color="text.secondary">Status:</Typography>
                <Box><Chip label={order.status.toUpperCase()} color={getStatusColor(order.status)} size="small" /></Box>
                <Typography color="text.secondary">Created By:</Typography>
                <Typography>{`${order.created_by_name || ''} ${order.created_by_lastname || ''}`.trim()}</Typography>
                <Typography color="text.secondary">Created At:</Typography>
                <Typography>{format(new Date(order.created_at), 'MMM dd, yyyy HH:mm')}</Typography>
              </Box>
            </CardContent>
          </Card>
        </Box>

        {/* Notes */}
        {order.notes && (
          <Card variant="outlined" sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>Notes</Typography>
              <Typography>{order.notes}</Typography>
            </CardContent>
          </Card>
        )}

        {/* Items table */}
        <Typography variant="h6" gutterBottom>
          Order Items ({(order.items || []).length})
        </Typography>
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell width={50}>Image</TableCell>
                <TableCell>SKU</TableCell>
                <TableCell>Product Name</TableCell>
                <TableCell>Size</TableCell>
                <TableCell align="center">Box Qty</TableCell>
                <TableCell align="center">Single Qty</TableCell>
                <TableCell>Each Price</TableCell>
                <TableCell>Offer Price</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(order.items || []).map((item, index) => {
                const boxes = parseInt(item.box_quantity) || 0;
                const singles = parseInt(item.single_quantity) || 0;

                return (
                  <TableRow key={item.id || index}>
                    <TableCell>
                      <Box
                        component="img"
                        src={getImageUrl(item.image_url || null)}
                        alt={item.product_name}
                        onClick={() => handleImagePreview(getImageUrl(item.image_url || null), item.product_name)}
                        sx={{ width: 40, height: 40, objectFit: 'cover', borderRadius: 1, cursor: 'pointer', transition: 'transform 0.2s', '&:hover': { transform: 'scale(1.1)' } }}
                      />
                    </TableCell>
                    <TableCell>{item.product_sku}</TableCell>
                    <TableCell>{item.product_name}</TableCell>
                    <TableCell>{item.unit_size && item.qty_in_box ? `${item.unit_size} x ${item.qty_in_box}` : (item.unit_size || '-')}</TableCell>
                    <TableCell align="center">{boxes}</TableCell>
                    <TableCell align="center">{singles}</TableCell>
                    <TableCell>{item.each_price || '-'}</TableCell>
                    <TableCell>{item.offer_price || '-'}</TableCell>
                  </TableRow>
                );
              })}
              {/* Totals row */}
              <TableRow>
                <TableCell colSpan={4} align="right">
                  <Typography fontWeight="bold">Totals:</Typography>
                </TableCell>
                <TableCell align="center"><Typography fontWeight="bold">{totalBoxQty}</Typography></TableCell>
                <TableCell align="center"><Typography fontWeight="bold">{totalSingleQty}</Typography></TableCell>
                <TableCell />
                <TableCell />
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* ---- Status Change Dialog ---- */}
      <Dialog open={statusDialogOpen} onClose={() => setStatusDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Change Order Status</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 1 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Current status: <Chip label={order.status.toUpperCase()} color={getStatusColor(order.status)} size="small" />
            </Typography>
            <FormControl fullWidth>
              <InputLabel>New Status</InputLabel>
              <Select value={newStatus} label="New Status" onChange={(e) => setNewStatus(e.target.value)}>
                {['draft', 'pending', 'sent', 'cancelled']
                  .filter(s => s !== order.status)
                  .map(s => (
                    <MenuItem key={s} value={s}>{s.toUpperCase()}</MenuItem>
                  ))
                }
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setStatusDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleStatusChange} disabled={updatingStatus || !newStatus}>
            {updatingStatus ? 'Updating...' : 'Update Status'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ---- Delete Confirmation Dialog ---- */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Delete Supplier Order</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete order {order.order_number}? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleDelete} color="error" disabled={deleting}
            startIcon={deleting ? <CircularProgress size={16} /> : null}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Image Preview Dialog */}
      <Dialog
        open={imagePreviewOpen}
        onClose={handleCloseImagePreview}
        maxWidth="md"
        fullWidth
        sx={{
          '& .MuiDialog-paper': {
            bgcolor: 'rgba(0, 0, 0, 0.9)',
            backdropFilter: 'blur(10px)',
          }
        }}
      >
        <DialogContent sx={{ p: 0, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', p: 2 }}>
            <Typography variant="h6" sx={{ color: 'white', flexGrow: 1 }}>
              {previewImage?.name}
            </Typography>
            <IconButton onClick={handleCloseImagePreview} sx={{ color: 'white' }} size="large">
              <ClearIcon />
            </IconButton>
          </Box>
          {previewImage && (
            <Box
              component="img"
              src={previewImage.url}
              alt={previewImage.name}
              onError={(e) => { (e.target as HTMLImageElement).src = getImageUrl(null); }}
              sx={{ maxWidth: '100%', maxHeight: '70vh', objectFit: 'contain', borderRadius: 1, cursor: 'pointer' }}
              onClick={handleCloseImagePreview}
            />
          )}
          <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)', textAlign: 'center', p: 2, fontSize: '0.875rem' }}>
            Tap image or close button to exit
          </Typography>
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default SupplierOrderDetail;
